/**
 * Role catalog — mirror of /app/backend/roles.py. Single source of truth on
 * the frontend for role labels, descriptions, hierarchy, and capability
 * helpers used across User Management, RoleGate, sidebar visibility, etc.
 *
 * Keep in sync with backend/roles.py.
 */

export const ROLES = [
  { key: "super_admin",         label: "Super Admin",         group: "Executive",        desc: "Full system access and configuration." },
  { key: "ceo",                 label: "CEO / Chief Executive",group: "Executive",       desc: "Executive oversight, approvals, all modules." },
  { key: "admin",               label: "Admin",               group: "Executive",        desc: "System administration, all modules." },
  { key: "management",          label: "Management",          group: "Executive",        desc: "Management dashboards, reports, approvals." },
  { key: "finance_manager",     label: "Finance Manager",     group: "Finance",          desc: "Full finance authority — approvals, packs, reports." },
  { key: "finance_officer",     label: "Finance Officer",     group: "Finance",          desc: "Day-to-day finance operations and entries." },
  { key: "hr_manager",          label: "HR Manager",          group: "Human Resources",  desc: "Full HR authority — payroll, contracts, discipline." },
  { key: "hr_officer",          label: "HR Officer",          group: "Human Resources",  desc: "Day-to-day HR operations." },
  { key: "procurement_manager", label: "Procurement Manager", group: "Procurement",      desc: "Full procurement authority — vendors, POs, approvals." },
  { key: "procurement_officer", label: "Procurement Officer", group: "Procurement",      desc: "Day-to-day procurement operations." },
  { key: "project_manager",     label: "Project Manager",     group: "Projects",         desc: "Manages assigned projects, deliverables, project tasks." },
  { key: "compliance_officer",  label: "Compliance Officer",  group: "Compliance",       desc: "Compliance tracker, renewals, regulatory documents." },
  { key: "asset_manager",       label: "Asset Manager",       group: "Assets",           desc: "Asset register, calibration, maintenance, rentals." },
  { key: "branch_user",         label: "Branch User",         group: "Branches",         desc: "Branch-scoped operations — sees ONLY records of the assigned branch." },
  { key: "staff",               label: "Staff / Employee",    group: "Staff",            desc: "Personal dashboard — own tasks, calendar, records." },
];

export const ROLE_GROUPS = ["Executive", "Finance", "Human Resources", "Procurement", "Projects", "Compliance", "Assets", "Branches", "Staff"];

// Numeric hierarchy — mirrors backend/roles.py ROLE_RANK
export const ROLE_RANK = {
  staff: 1,
  branch_user: 1,
  finance_officer: 1,
  hr_officer: 1,
  procurement_officer: 1,
  compliance_officer: 2,
  asset_manager: 2,
  finance_manager: 2,
  hr_manager: 2,
  procurement_manager: 2,
  project_manager: 2,
  manager: 2,       // legacy
  management: 3,
  admin: 4,
  ceo: 5,
  super_admin: 6,
};

export function roleLabel(roleKey) {
  if (!roleKey) return "—";
  if (roleKey === "manager") return "Manager (legacy)";
  return ROLES.find((r) => r.key === roleKey)?.label || roleKey.replace(/_/g, " ");
}

export function roleGroup(roleKey) {
  if (!roleKey) return "—";
  if (roleKey === "manager") return "Legacy";
  return ROLES.find((r) => r.key === roleKey)?.group || "—";
}

// ── Capability helpers (mirror backend/roles.py) ────────────────────────
const EXECUTIVE = new Set(["super_admin", "ceo", "admin", "management"]);

/**
 * Permission Access Management check. Returns:
 *   true/false — when the user object carries the resolved `permissions`
 *                array (attached by /auth/me); authoritative.
 *   null       — no permission data present (fall back to role logic).
 */
export function can(u, action) {
  if (u?.role === "super_admin") return true;
  if (Array.isArray(u?.permissions)) return u.permissions.includes(action);
  return null;
}

function legacyManager(u, scope) {
  if (u?.role !== "manager") return false;
  const s = (u?.dept_scope || "").toLowerCase();
  return s === scope || s === "" || s === "_none";
}

export const isExecutive       = (u) => EXECUTIVE.has(u?.role);
export const isAdminOrAbove    = (u) => ["super_admin", "ceo", "admin"].includes(u?.role);
export const isFinance         = (u) => can(u, "finance.view") ?? (["finance_manager", "finance_officer"].includes(u?.role) || isExecutive(u) || legacyManager(u, "finance"));
export const isFinanceManager  = (u) => u?.role === "finance_manager" || isAdminOrAbove(u) || legacyManager(u, "finance");
export const isHR              = (u) => can(u, "hr.view") ?? (["hr_manager", "hr_officer"].includes(u?.role) || isExecutive(u) || legacyManager(u, "hr"));
export const isHRManager       = (u) => u?.role === "hr_manager" || isAdminOrAbove(u) || legacyManager(u, "hr");
export const isProcurement     = (u) => can(u, "procurement.view") ?? (["procurement_manager", "procurement_officer"].includes(u?.role) || isExecutive(u) || legacyManager(u, "pm"));
export const isProcurementMgr  = (u) => u?.role === "procurement_manager" || isAdminOrAbove(u) || legacyManager(u, "pm");
export const isProjectManager  = (u) => can(u, "projects.view") ?? (u?.role === "project_manager" || isExecutive(u) || legacyManager(u, "pm"));
export const isCompliance      = (u) => can(u, "compliance.view") ?? (u?.role === "compliance_officer" || isExecutive(u) || legacyManager(u, "compliance"));
export const isAssetManager    = (u) => can(u, "assets.view") ?? (u?.role === "asset_manager" || isExecutive(u) || legacyManager(u, "pm"));

export function isStaffOnly(u) {
  return u?.role === "staff";
}

export function isBranchUser(u) {
  return u?.role === "branch_user";
}
